// Schemas and types for the video tool, shared by server and UI.
import { z } from 'zod';

export const VIDEO_PROJECT_VERSION = 1;
export const VIDEO_PROJECT_EXTENSION = 'dhvideo';
/** Longest timeline the editor and the exporter accept (seconds). */
export const VIDEO_MAX_DURATION = 4 * 60 * 60;
/** Shortest clip or text item the timeline can hold (seconds). */
export const VIDEO_MIN_CLIP = 0.1;

const itemId = z.string().min(1).max(64);
const seconds = z.number().finite().min(0).max(VIDEO_MAX_DURATION);
/** Normalized canvas coordinate; slightly outside 0–1 so overlays can hang off the edge. */
const unit = z.number().finite().min(-1).max(2);
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);

export const videoCanvas = z.object({
  width: z.number().int().min(16).max(3840).multipleOf(2),
  height: z.number().int().min(16).max(2160).multipleOf(2),
  fps: z.number().int().min(1).max(60),
});
export type VideoCanvas = z.infer<typeof videoCanvas>;

const clipFields = {
  id: itemId,
  fileId: z.uuid(),
  /** Timeline position of the clip's first frame. */
  start: seconds,
  /** Source range used, in media seconds. */
  in: seconds,
  out: seconds,
  /** Linear gain, 1 = unchanged. */
  volume: z.number().finite().min(0).max(2),
  muted: z.boolean(),
};
const sourceRange = (clip: { in: number; out: number }) => clip.out - clip.in >= VIDEO_MIN_CLIP - 1e-6;
const rangeMessage = { message: 'out must be after in' };

export const videoClip = z.object(clipFields).refine(sourceRange, rangeMessage);
export type VideoClip = z.infer<typeof videoClip>;

export const overlayClip = z
  .object({
    ...clipFields,
    /** Center of the overlay, normalized to the canvas. */
    x: unit,
    y: unit,
    /** Overlay width as a fraction of the canvas width. */
    scale: z.number().finite().min(0.05).max(1),
  })
  .refine(sourceRange, rangeMessage);
export type OverlayClip = z.infer<typeof overlayClip>;

export const TEXT_ALIGNS = ['left', 'center', 'right'] as const;
export type TextAlign = (typeof TEXT_ALIGNS)[number];

export const textItem = z
  .object({
    id: itemId,
    text: z.string().max(500),
    start: seconds,
    end: seconds,
    /** Anchor point normalized to the canvas: `align` picks the horizontal anchor, y is the vertical center. */
    x: unit,
    y: unit,
    /** Font size in canvas pixels. */
    size: z.number().int().min(8).max(400),
    color: hexColor,
    align: z.enum(TEXT_ALIGNS),
  })
  .refine((item) => item.end - item.start >= VIDEO_MIN_CLIP - 1e-6, { message: 'end must be after start' });
export type TextItem = z.infer<typeof textItem>;

const MAX_ITEMS = 500;

export const videoProject = z.object({
  version: z.literal(VIDEO_PROJECT_VERSION),
  canvas: videoCanvas,
  tracks: z.object({
    video1: z.object({ hidden: z.boolean(), muted: z.boolean(), clips: z.array(videoClip).max(MAX_ITEMS) }),
    /** Overlay layer drawn above Video 1 (picture-in-picture). */
    video2: z.object({ hidden: z.boolean(), muted: z.boolean(), clips: z.array(overlayClip).max(MAX_ITEMS) }),
    audio: z.object({ muted: z.boolean(), clips: z.array(videoClip).max(MAX_ITEMS) }),
    text: z.object({ hidden: z.boolean(), items: z.array(textItem).max(MAX_ITEMS) }),
  }),
});
export type VideoProject = z.infer<typeof videoProject>;
export type VideoTrackId = keyof VideoProject['tracks'];

export const DEFAULT_VIDEO_CANVAS: VideoCanvas = { width: 1920, height: 1080, fps: 30 };

export function emptyVideoProject(canvas: VideoCanvas = DEFAULT_VIDEO_CANVAS): VideoProject {
  return {
    version: VIDEO_PROJECT_VERSION,
    canvas,
    tracks: {
      video1: { hidden: false, muted: false, clips: [] },
      video2: { hidden: false, muted: false, clips: [] },
      audio: { muted: false, clips: [] },
      text: { hidden: false, items: [] },
    },
  };
}

export const MEDIA_KINDS = ['video', 'audio', 'image'] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

export const videoProbeInput = z.object({ fileId: z.uuid() });

export interface VideoProbe {
  kind: MediaKind;
  /** Seconds; 0 for still images. */
  duration: number;
  width: number;
  height: number;
  hasAudio: boolean;
  hasVideo: boolean;
}

export const videoThumbnailQuery = z.object({
  fileId: z.uuid(),
  t: z.coerce.number().finite().min(0).max(VIDEO_MAX_DURATION).default(0),
  /** Thumbnail height in pixels. */
  h: z.coerce.number().int().min(24).max(360).default(90),
});

export const videoWaveformQuery = z.object({ fileId: z.uuid() });

export const videoExportInput = z.object({
  projectFileId: z.uuid(),
  name: z.string().trim().min(1).max(240),
});

export const VIDEO_JOB_STATUSES = ['queued', 'running', 'done', 'failed'] as const;
export type VideoJobStatus = (typeof VIDEO_JOB_STATUSES)[number];

/** Stable failure codes; the UI turns them into sentences. */
export const VIDEO_EXPORT_ERRORS = [
  'empty_project',
  'invalid_project',
  'missing_media',
  'unreadable_media',
  'text_unsupported',
  'timeout',
  'render_failed',
  'interrupted',
] as const;
export type VideoExportError = (typeof VIDEO_EXPORT_ERRORS)[number];

export interface VideoJob {
  id: string;
  status: VideoJobStatus;
  /** 0–1. */
  progress: number;
  resultFileId: string | null;
  error: VideoExportError | null;
  createdAt: string;
}

export const clipLength = (clip: { in: number; out: number }) => clip.out - clip.in;
export const clipEnd = (clip: { start: number; in: number; out: number }) => clip.start + clipLength(clip);

/** Timeline length: the end of the last clip or text item on any track. */
export function projectDuration(project: VideoProject): number {
  const { video1, video2, audio, text } = project.tracks;
  return Math.max(
    0,
    ...video1.clips.map(clipEnd),
    ...video2.clips.map(clipEnd),
    ...audio.clips.map(clipEnd),
    ...text.items.map((item) => item.end),
  );
}
